'use client';
import { useEffect, useRef, useState } from 'react';
import MuiDialog from '@mui/material/Dialog';
import InputBase from '@mui/material/InputBase';
import Button from '@/components/Button';
import Title from '@/components/Title';
import SlideTransition from '@/components/SlideTransition';
import ResultsList from './ResultsList';
import { useAppState } from '@/hooks/useAppState';
import {
  useAllCompanies,
  useAllInvestors,
  useDispatch,
} from '@/hooks/useStore';
import { setCompanies as dispatchSetCompanies } from '@/store/slices/companiesSlice';
import { setInvestors as dispatchSetInvestors } from '@/store/slices/investorsSlice';
import EmptyMessage from './EmptyMessage';
import { usePath } from '@/hooks/usePath';
import { getCompanyUrl, getInvestorUrl } from '@/api/getUrl';
import {
  classes,
  fetchSearchableData,
  getMatchingCompanies,
  getMatchingInvestors,
  getRecentCompanies,
  getRecentInvestors,
} from './utils';

export type SearchDialogProps = {
  onClose: () => void;
};

export default function SearchDialog({ onClose }: SearchDialogProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  const { navigate } = usePath();
  const {
    onApiError,
    recentCompanyIds,
    recentInvestorIds,
    onRemoveRecentCompanyIds,
    onRemoveRecentInvestorIds,
    onAddRecentCompanyId,
    onAddRecentInvestorId,
  } = useAppState();
  const companies = useAllCompanies();
  const investors = useAllInvestors();
  const [search, setSearch] = useState('');

  useEffect(() => {
    handleFetchData();
  }, []);

  const handleFetchData = async () => {
    // NOTE: Just fetching a bunch of companies and investors for now -- will probably eventually need actual pagination
    try {
      const data = await fetchSearchableData();
      dispatch(dispatchSetCompanies(data.companies));
      dispatch(dispatchSetInvestors(data.investors));
    } catch (err) {
      onApiError(err, 'Error fetching results');
    }
  };

  const handleClearRecents = () => {
    onRemoveRecentCompanyIds();
    onRemoveRecentInvestorIds();
    if (inputRef.current) inputRef.current.focus();
  };

  const handleSelectCompany = (companyId: number) => {
    onAddRecentCompanyId(companyId);
    navigate(getCompanyUrl(companyId));
    onClose();
  };

  const handleSelectInvestor = (investorId: number) => {
    onAddRecentInvestorId(investorId);
    navigate(getInvestorUrl(investorId));
    onClose();
  };

  const renderCompanies = () => {
    const isRecents = search.length === 0;
    const results = isRecents
      ? getRecentCompanies(recentCompanyIds, companies)
      : getMatchingCompanies(search, companies);
    if (search.length === 0 && results.length === 0) return null;
    return (
      <div className={classes.resultsSection}>
        <Title className={classes.resultsTitle} size="md">
          {`${isRecents ? 'Recent ' : ''}Companies`}
        </Title>
        {results.length > 0 ? (
          <ResultsList results={results} onSelect={handleSelectCompany} />
        ) : (
          <EmptyMessage>No matching companies</EmptyMessage>
        )}
      </div>
    );
  };

  const renderInvestors = () => {
    const isRecents = search.length === 0;
    const results = isRecents
      ? getRecentInvestors(recentInvestorIds, investors)
      : getMatchingInvestors(search, investors);
    if (search.length === 0 && results.length === 0) return null;
    return (
      <div className={classes.resultsSection}>
        <Title className={classes.resultsTitle} size="md">
          {`${isRecents ? 'Recent ' : ''}Investors`}
        </Title>
        {results.length > 0 ? (
          <ResultsList results={results} onSelect={handleSelectInvestor} />
        ) : (
          <EmptyMessage>No matching investors</EmptyMessage>
        )}
      </div>
    );
  };

  return (
    <MuiDialog
      open
      keepMounted
      disableRestoreFocus
      maxWidth={false}
      TransitionComponent={SlideTransition}
      PaperProps={{ className: classes.paper }}
      onClose={onClose}>
      <div className={classes.wrapper}>
        <InputBase
          autoFocus
          inputRef={inputRef}
          autoComplete="off"
          className={classes.inputBase}
          name="search"
          placeholder="Search for companies & investors..."
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search.length === 0 &&
          [...recentCompanyIds, ...recentInvestorIds].length > 0 && (
            <div className={classes.recentlyViewedHeader}>
              <Button
                variant="text"
                tabIndex={-1 /* Tab straight into results list */}
                textClasses={classes.clearButton}
                onClick={handleClearRecents}>
                Clear recent
              </Button>
            </div>
          )}
        {renderCompanies()}
        {renderInvestors()}
      </div>
    </MuiDialog>
  );
}
